// src/components/cctv/CCTVList.js
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { ExternalLink, Video, RefreshCcw, X } from 'lucide-react';

import { useAtom } from 'jotai';
import { activeChildDetail, appConstants, cctvIntial, tab } from '../../atomstore/store';
import { Capacitor } from '@capacitor/core';

import {
    checkCCTVConnectionFunction,
    ReloadData,
    fetchEmbedHash,
    exchangeCctvHybridAppAuth,
    getCctvDueInvoice,
    checkCctvChildPresence,
} from "../../queryfunctions/queryfunctions";

import { useRequestProcessor } from "../../requestProcessor";



import CameraView from './CameraView';
import { getCctvPlayerMode, isAbsoluteHttpUrl } from './cctvPlayerMode';
import { useNavigate } from 'react-router-dom';
import SpinnerLoading from "../common/SpinnerLoading";
import { isEnabledByDefault } from "../../utils/configFlag";

const normalizeDueInvoiceReminder = (response) => {
    if (
        response?.has_due_invoice !== true ||
        typeof response?.message !== "string" ||
        !response.message.trim() ||
        typeof response?.payment_link !== "string" ||
        !response.payment_link.trim()
    ) {
        return null;
    }

    return {
        message: response.message,
        paymentLink: response.payment_link,
        displayMode: response.display_mode === "continue" ? "continue" : "one_time",
    };
};


const CCTVList = () => {
    const navigate = useNavigate()
    const [, setActiveTab] = useAtom(tab)
    const [cctvState, setCctvState] = useAtom(cctvIntial);
    const [activeChild] = useAtom(activeChildDetail);
    const [runtimeConstants] = useAtom(appConstants);

    const [streamError, setStreamError] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [childOnPause, setChildOnPause] = useState(false);
    const [isOneTimeDueReminderDismissed, setIsOneTimeDueReminderDismissed] = useState(false);

    const { useMutationWrapper, useQueryWrapper } = useRequestProcessor();
    const restrictCctvByAttendance = isEnabledByDefault(runtimeConstants?.restrictCctvByAttendance);
    const showCctvDueInvoiceReminder = isEnabledByDefault(runtimeConstants?.showCctvDueInvoiceReminder);

    const dueInvoiceQuery = useQueryWrapper(
        ["cctv-due-invoice", activeChild?.id],
        () => getCctvDueInvoice(activeChild),
        {
            enabled: showCctvDueInvoiceReminder && !!activeChild?.id,
            retry: 0,
            refetchOnWindowFocus: false,
        }
    );
    const dueInvoiceReminder = showCctvDueInvoiceReminder
        ? normalizeDueInvoiceReminder(dueInvoiceQuery?.data)
        : null;

    const presenceQuery = useQueryWrapper(
        ["cctv-child-presence", activeChild?.id, activeChild?.length],
        () => checkCctvChildPresence(activeChild),
        {
            enabled:
                restrictCctvByAttendance &&
                !!activeChild?.id &&
                !!activeChild?.length,
            retry: 0,
            refetchOnWindowFocus: true,
        }
    );
    const isChildPresent = presenceQuery?.data === true;

    useEffect(() => {
        setIsOneTimeDueReminderDismissed(false);
    }, [activeChild?.id, dueInvoiceReminder?.message, dueInvoiceReminder?.paymentLink]);

    // Derive playback mode purely from the stream URL:
    //   legacy      → fp.streamonweb.com / chennaistream.net (old token path)
    //   embed_test  → test.streamonweb.com (POST-embed + hash, no live flag)
    //   embed_live  → aivmsfp.streamonweb.com (POST-embed + hash with live: true)
    const streamLink = activeChild?.html5_player_link || "";
    const cctvPlayerMode = getCctvPlayerMode(streamLink);
    const useLegacyCctvPlayback = cctvPlayerMode === "legacy";
    const useEmbedStylePlayback =
        cctvPlayerMode === "embed_test" || cctvPlayerMode === "embed_live";
    const embedHashLive = cctvPlayerMode === "embed_live";

    /* ---------------------------------
       TOKEN VALIDATION
    -------------------------------- */
    const getValidToken = (tokens = []) => {
        const nowUTC = moment.utc();
        return tokens.find(({ start, end }) => {
            const s = moment.utc(start);
            const e = moment.utc(end);
            return nowUTC.isBetween(s, e);
        })?.token;
    };

    /* ---------------------------------
       HASH FOR EMBEDDED CCTV (MD5)
       Used for StreamOnWeb-style POST-based embeds
    -------------------------------- */
    // Hybrid auth only applies inside the native Capacitor runtime (not browser).
    const isHybridApp = useEmbedStylePlayback && Capacitor?.isNativePlatform?.() === true;
    const streamUrlObj = (() => {
        try {
            return streamLink ? new URL(streamLink) : null;
        } catch {
            return null;
        }
    })();
    const streamOrigin = streamUrlObj?.origin || null;
    const centerSlug = (() => {
        if (!streamUrlObj) return null;
        const segments = streamUrlObj.pathname.split("/").filter(Boolean);
        if (!segments || segments.length < 2) return null;
        if (segments[0] === "center" || segments[0] === "c") return segments[1];
        return null;
    })();
    const hybridCenterPageUrl =
        useEmbedStylePlayback && streamOrigin && centerSlug
            ? `${streamOrigin}/center/${centerSlug}`
            : null;
    const hybridAppAuthUrl =
        hybridCenterPageUrl ? `${hybridCenterPageUrl}/app-auth` : null;

    const hybridAuthEnabled =
        isHybridApp &&
        !!hybridAppAuthUrl &&
        !!hybridCenterPageUrl;

    const hybridAuthQuery = useQueryWrapper(
        ["cctv-hybrid-auth", hybridAppAuthUrl, embedHashLive],
        () => exchangeCctvHybridAppAuth({ appAuthUrl: hybridAppAuthUrl, live: embedHashLive }),
        {
            enabled: hybridAuthEnabled && (!restrictCctvByAttendance || isChildPresent),
            retry: 0,
        }
    );

    const hybridSessionToken = hybridAuthQuery?.data?.app_session_token || null;
    // Never point the iframe at the bare center page. Without app_session the
    // frame is unauthenticated, and a failed load can fall back to this app.
    const hybridSrc =
        hybridAuthEnabled && hybridCenterPageUrl && hybridSessionToken
            ? `${hybridCenterPageUrl}?app_session=${encodeURIComponent(hybridSessionToken)}`
            : null;

    const embedHashQuery = useQueryWrapper(
        ["cctv-embed-hash", activeChild?.id, activeChild?.center_id, embedHashLive],
        () => fetchEmbedHash({ live: embedHashLive }),
        {
            enabled: useEmbedStylePlayback && !!activeChild && !hybridAuthEnabled && (!restrictCctvByAttendance || isChildPresent),
        }
    );
    const embedHash = embedHashQuery?.data;

    /* ---------------------------------
       MUTATIONS
    -------------------------------- */
    const checkConnection = useMutationWrapper("checkCctv", () =>
        checkCCTVConnectionFunction(cctvState, setCctvState, activeChild)
    );

    const reloadToken = useMutationWrapper("reload", () =>
        ReloadData().then(() => {
            setStreamError(false);
            setRefreshKey(k => k + 1);
            checkConnection.mutate();
        })
    );

    /* ---------------------------------
       INITIAL LOAD
    -------------------------------- */
    useEffect(() => {
        if (!activeChild) return;

        setChildOnPause(activeChild.pause_state === 1);

        if (useLegacyCctvPlayback) {
            // Legacy: always need a valid access token for the stream URL.
            const token = getValidToken(activeChild?.cctv_access_key?.token);
            if (!token) setStreamError(true);
        }
        // embed_test / embed_live: hash-based embed, no access token needed.

        checkConnection.mutate();
        // We intentionally do NOT include dependencies here beyond initial mount,
        // because downstream CCTV APIs and state assume this runs only once per screen load.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---------------------------------
      BUILD FINAL STREAM URL (for direct streams)
      - Legacy direct links continue to use token-based URLs
      - StreamOnWeb-style centers use POST-based iframe embed instead,
        so we do NOT build a direct stream URL for them.
    -------------------------------- */
    const buildStreamUrl = () => {
        // Embed modes use POST-based iframe; no direct stream URL needed.
        if (useEmbedStylePlayback) return null;

        const authToken = getValidToken(activeChild?.cctv_access_key?.token);
        if (!authToken) return null;

        const url = `${activeChild?.html5_player_link}/${activeChild?.center_id}/${activeChild?.id}/${authToken}`;
        // A relative or malformed link resolves against this app and the
        // catch-all route renders Home inside the camera iframe.
        return isAbsoluteHttpUrl(url) ? url : null;
    };

    const streamUrl = buildStreamUrl();

    const usePostEmbed =
        useEmbedStylePlayback &&
        !!activeChild &&
        !hybridAuthEnabled &&
        isAbsoluteHttpUrl(activeChild?.html5_player_link) &&
        !!embedHash?.id1 &&
        !!embedHash?.id2 &&
        !!embedHash?.hash;

    const embedUrl = usePostEmbed ? activeChild?.html5_player_link : null;

    /* ---------------------------------
       LIST UI WITH STATE CHECKS
    -------------------------------- */
    const openDueInvoicePayment = () => {
        if (!dueInvoiceReminder?.paymentLink) return;

        window.open(
            dueInvoiceReminder.paymentLink,
            "_blank",
            "location=no,closebuttoncaption=Back"
        );
    };

    const shouldShowDueInvoiceReminder =
        dueInvoiceReminder &&
        (
            dueInvoiceReminder.displayMode === "continue" ||
            !isOneTimeDueReminderDismissed
        );

    const renderDueInvoiceReminder = () => {
        if (!shouldShowDueInvoiceReminder) return null;

        return (
            <div
                className="w-full bg-black px-3 py-2.5 text-left"
                role="status"
                aria-live="polite"
                aria-labelledby="due-invoice-title"
            >
                <div className="flex items-center gap-3">
                    <p id="due-invoice-title" className="min-w-0 flex-1 text-sm font-medium leading-snug text-white">
                        {dueInvoiceReminder.message}
                    </p>
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            onClick={openDueInvoicePayment}
                            className="inline-flex items-center justify-center gap-1 rounded-lg bg-cyan-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-cyan-600 active:scale-[0.98]"
                        >
                            Pay now <ExternalLink className="h-4 w-4" />
                        </button>
                        {dueInvoiceReminder.displayMode === "one_time" && (
                            <button
                                type="button"
                                className="flex h-8 w-8 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white active:scale-[0.98]"
                                onClick={() => setIsOneTimeDueReminderDismissed(true)}
                                aria-label="Dismiss"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderBlock = (title, subtitle, action = null) => (
        <>
            <div className="sticky top-0 z-10">
                {renderDueInvoiceReminder()}
            </div>
            <div className="p-6  text-center my-4">
                <Video className="w-12 h-12 text-gray-600 mx-auto mb-4 opacity-40" />

                <p className="font-semibold text-lg">{title}</p>
                {subtitle && <p className="text-gray-400 text-sm mt-2">{subtitle}</p>}

                {action && (
                    <button
                        onClick={action.onClick}
                        className="px-5 py-2 mt-4 bg-orange-500 rounded-lg flex items-center space-x-2 mx-auto text-white"
                    >
                        <RefreshCcw className="w-4 h-4" />
                        <span>{action.label}</span>
                    </button>
                )}
            </div>
        </>
    );

    console.log("Constants at cctv", runtimeConstants)

    /* ---------------------------------
       CONDITIONS BEFORE DISPLAYING LIST
    -------------------------------- */

    if (childOnPause) {
        return renderBlock(
            runtimeConstants?.cameraPauseTitle,
            runtimeConstants?.cameraPauseSubtitle
        );
    }

    if (cctvState.centerCctvDown === true) {
        return renderBlock(
            runtimeConstants?.cctvServerDownTitle ||
            "Internet at the center is not working",
            runtimeConstants?.cctvServerDownMessage ||
            "We will notify you once the connection is restored.",
            {
                label: checkConnection.isLoading ? "Refreshing..." : "Refresh",
                onClick: () => checkConnection.mutate(),
            }
        );
    }

    if (restrictCctvByAttendance && presenceQuery.isLoading) {
        return <SpinnerLoading />;
    }

    if (restrictCctvByAttendance && !isChildPresent) {
        return renderBlock(
            runtimeConstants?.cctvChildNotPresentTitle || "Camera Offline",
            runtimeConstants?.cctvChildNotPresentMessage ||
                "Live stream is available only while your child is at school.",
            {
                label: presenceQuery.isFetching ? "Refreshing..." : "Refresh",
                onClick: () => presenceQuery.refetch(),
            }
        );
    }

    if (streamError) {
        return renderBlock(
            "Livestream Not Available",
            "Please refresh to generate a new secure token.",
            {
                label: reloadToken.isLoading ? "Refreshing..." : "Refresh Token",
                onClick: () => reloadToken.mutate(),
            }
        );
    }

    const embedAuthEnabled =
        useEmbedStylePlayback &&
        !!activeChild &&
        !hybridAuthEnabled &&
        (!restrictCctvByAttendance || isChildPresent);

    // CameraView (and its back button) only mounts once a real player URL exists.
    if (hybridAuthEnabled && !hybridSessionToken) {
        const authFailed =
            hybridAuthQuery.isError ||
            (hybridAuthQuery.isFetched && !hybridAuthQuery.isFetching);
        if (authFailed) {
            return renderBlock(
                "Livestream Not Available",
                "Please refresh to try the camera again.",
                {
                    label: hybridAuthQuery.isFetching ? "Refreshing..." : "Refresh",
                    onClick: () => hybridAuthQuery.refetch(),
                }
            );
        }
        return <SpinnerLoading />;
    }

    if (embedAuthEnabled && !usePostEmbed) {
        const hashFailed =
            embedHashQuery.isError ||
            (embedHashQuery.isFetched && !embedHashQuery.isFetching);
        if (hashFailed) {
            return renderBlock(
                "Livestream Not Available",
                "Please refresh to try the camera again.",
                {
                    label: embedHashQuery.isFetching ? "Refreshing..." : "Refresh",
                    onClick: () => embedHashQuery.refetch(),
                }
            );
        }
        return <SpinnerLoading />;
    }

    if (useLegacyCctvPlayback && !isAbsoluteHttpUrl(streamUrl)) {
        return renderBlock(
            "Livestream Not Available",
            "Please refresh to generate a new secure token.",
            {
                label: reloadToken.isLoading ? "Refreshing..." : "Refresh Token",
                onClick: () => reloadToken.mutate(),
            }
        );
    }

    return (
        <CameraView
            streamUrl={streamUrl}
            key={refreshKey}
            useLegacyPlayer={useLegacyCctvPlayback}
            usePostEmbed={usePostEmbed}
            embedUrl={embedUrl}
            embedParams={embedHash}
            hybridAuth={
                hybridAuthEnabled
                    ? {
                        enabled: true,
                        centerPageUrl: hybridCenterPageUrl,
                        src: hybridSrc,
                        isLoading: hybridAuthQuery?.isLoading === true,
                        hasError: hybridAuthQuery?.isError === true,
                    }
                    : { enabled: false }
            }
            playerMode={cctvPlayerMode}
            topBanner={renderDueInvoiceReminder()}
            onBack={() => {
                // Prefer an explicit route over navigate(-1): browser history and the
                // app's navStack can diverge (BottomNav / Home both push /cctv), so
                // the first -1 sometimes lands on another CCTV entry or a dead entry
                // and it feels like the button needs two taps.
                setActiveTab("home");
                navigate("/home");
            }}
        />
    );

    /* ---------------------------------
       MAIN CAMERA LIST
    -------------------------------- */
    // return (
    //     <div className="p-4 space-y-3">
    //         <h2 className="text-xl font-bold text-gray-800 mb-4">Live Camera Feed</h2>

    //         {(reloadToken.isLoading || checkConnection.isLoading) && (
    //             <SpinnerLoading />
    //         )}

    //         {cameras.map((camera) => (
    //             <button
    //                 key={camera.id}
    //                 onClick={() => {
    //                     setCurrentScreen('camera-view')
    //                     setCamera(streamUrl)
    //                 }}
    //                 className="w-full bg-white rounded-2xl p-4 shadow-sm border border-gray-100 active:scale-95 transition"
    //             >
    //                 <div className="flex items-center justify-between">
    //                     <div className="flex items-center space-x-3">
    //                         <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center">
    //                             <Video className="w-6 h-6 text-white" />
    //                         </div>
    //                         <div className="text-left">
    //                             <p className="font-semibold text-gray-800">{camera.name}</p>
    //                             <p className="text-sm text-gray-500">{camera.location}</p>
    //                         </div>
    //                     </div>

    //                     <div className="flex items-center space-x-2">
    //                         <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
    //                         <span className="text-sm text-green-600 font-medium">Live</span>
    //                     </div>
    //                 </div>
    //             </button>
    //         ))}
    //     </div>
    // );
};

export default CCTVList;
